import { motion } from "framer-motion";
import {
  ShieldCheck, Landmark, Megaphone, Handshake, Sparkles, Award,
  HeartHandshake, Users, GraduationCap, Flag, Building2, ArrowRight,
} from "lucide-react";
import { SectionLabel } from "@/components/Section";
import { IMAGES, SPONSOR_DEADLINES } from "@/data/content";
import { useSponsorConfig } from "@/hooks/useSponsorConfig";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: "easeOut" } }),
};

const VALUE_PROPS = [
  { icon: ShieldCheck, title: "Support Those Who Serve", desc: "Stand alongside the men and women who dedicate their lives to protecting our families, responding to emergencies, and serving our communities with courage and commitment." },
  { icon: Landmark, title: "Build Your Community Legacy", desc: "Become part of a growing tradition dedicated to honoring service, strengthening communities, and creating meaningful impact through the Wayne Walker Foundation." },
  { icon: Megaphone, title: "Strengthen Your Brand", desc: "Receive year-round recognition through event marketing, website placement, social media, printed materials, and on-site branding while aligning your company with leadership, integrity, and service." },
  { icon: Handshake, title: "Build Meaningful Relationships", desc: "Host clients, connect with community leaders, reward employees, and develop valuable relationships in an authentic, purpose-driven environment." },
  { icon: Sparkles, title: "Inspire Your Team", desc: "Give employees an opportunity to participate in something bigger than themselves while demonstrating your organization's commitment to giving back." },
  { icon: Award, title: "Become a Founding Partner", desc: "Help establish what is intended to become one of the region's premier annual charitable events. Founding sponsors will always be recognized as the organizations that helped launch this tradition." },
];

const PILLARS = [
  { icon: HeartHandshake, title: "Community Impact", desc: "Help fund programs and initiatives that recognize and support first responders and strengthen the communities they serve." },
  { icon: Megaphone, title: "Brand Visibility", desc: "Increase awareness through digital marketing, event signage, social media, printed materials, and year-round sponsor recognition." },
  { icon: Handshake, title: "Business Relationships", desc: "Develop meaningful connections with business leaders, community partners, customers, and future clients." },
  { icon: Users, title: "Employee Engagement", desc: "Reward your team with a premium event experience while fostering pride in your company's commitment to community service." },
  { icon: Landmark, title: "Lasting Legacy", desc: "Become part of an annual tradition dedicated to honoring service, supporting first responders, and creating a lasting impact for future generations." },
];

const CHIPS = [
  { icon: ShieldCheck, label: "Leadership" },
  { icon: Landmark, label: "Legacy" },
  { icon: Building2, label: "Community" },
  { icon: HeartHandshake, label: "Service" },
  { icon: Handshake, label: "Partnership" },
];

export function WhySponsor() {
  const cfg = useSponsorConfig();
  const partners = cfg.partners || [];
  return (
    <div data-testid="why-sponsor">
      {/* INTRO */}
      <section className="paper-noise bg-background">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-6">
            <SectionLabel>Why Sponsor?</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-4xl sm:text-5xl text-espresso leading-[1.05]">
              More Than a Sponsorship—<span className="text-rust">An Investment in Those Who Serve</span> Our Communities.
            </h2>
            <div className="mt-6 space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed">
              <p>When you sponsor the Wayne Walker Foundation, you are doing more than supporting a golf tournament—you are investing in the people who answer the call when our communities need them most.</p>
              <p>Your partnership helps the Foundation fulfill its mission of honoring Wayne Walker's legacy by supporting firefighters, law enforcement officers, EMS professionals, and emergency dispatchers while building a tradition of service, leadership, and community impact that will endure for generations.</p>
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={1} className="lg:col-span-6 grid grid-cols-2 gap-4">
            <img src={IMAGES.ffTeam} alt="Firefighter on the scene" className="w-full h-72 object-cover border border-border hard-shadow-sm" />
            <div className="grid gap-4">
              <img src={IMAGES.policeService} alt="Police officer in service" className="w-full h-[8.5rem] object-cover border border-border hard-shadow-sm" />
              <img src={IMAGES.emsMedic} alt="Paramedic with ambulance" className="w-full h-[8.5rem] object-cover border border-border hard-shadow-sm" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE PROPOSITION — 6 cards */}
      <section className="bg-card paper-noise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <SectionLabel>The Opportunity</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl text-espresso">Why Partner With Us</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUE_PROPS.map((c, i) => (
              <motion.div
                key={c.title}
                initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i % 3}
                className="border border-border bg-background p-7 transition-transform hover:-translate-y-1 hard-shadow-sm"
              >
                <span className="h-12 w-12 bg-rust text-primary-foreground flex items-center justify-center"><c.icon size={22} /></span>
                <h3 className="mt-5 font-serif font-bold text-xl text-espresso">{c.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY THE WAYNE WALKER FOUNDATION — premium dark section */}
      <section className="relative bg-espresso text-cream overflow-hidden">
        <img src={IMAGES.responderGroup} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-espresso via-espresso/95 to-espresso/70" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-20 md:py-28">
          <SectionLabel>Why The Wayne Walker Foundation</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl sm:text-5xl">Building a Legacy of Service</h2>
          <p className="mt-6 text-lg text-rust font-serif italic">
            The Wayne Walker Foundation was established with a simple belief: communities are strongest when they invest in the people who dedicate their lives to serving others.
          </p>
          <div className="mt-6 space-y-5 text-base md:text-lg text-cream/80 leading-relaxed max-w-3xl">
            <p>Through fundraising events, community partnerships, and charitable initiatives, the Foundation seeks to recognize and support firefighters, law enforcement officers, EMS professionals, and emergency dispatchers while inspiring others to lead through generosity and service.</p>
            <p>This tournament represents more than a day of golf—it is the beginning of a long-term commitment to strengthening our communities through meaningful action.</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {CHIPS.map((chip) => (
              <div key={chip.label} className="flex items-center gap-2 border border-cream/25 bg-cream/5 px-4 py-2.5">
                <chip.icon size={16} className="text-rust" />
                <span className="label-font text-xs text-cream tracking-[0.1em]">{chip.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOUR SPONSORSHIP DELIVERS — 5 pillars */}
      <section className="paper-noise bg-background">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <SectionLabel>What Your Sponsorship Delivers</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl text-espresso">Five Pillars of Impact</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-0 lg:gap-0 border-l border-border">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="border-r border-t border-b border-border bg-card p-6"
              >
                <div className="flex items-center gap-3">
                  <span className="label-font text-3xl text-rust/30 font-serif font-black">{String(i + 1).padStart(2, "0")}</span>
                  <span className="h-10 w-10 bg-espresso text-cream flex items-center justify-center shrink-0"><p.icon size={18} /></span>
                </div>
                <h3 className="mt-4 font-serif font-bold text-lg text-espresso leading-tight">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDING PARTNER CALLOUT */}
      <section className="bg-card paper-noise">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 md:py-20 relative z-10">
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="relative border-2 border-rust bg-espresso text-cream p-8 sm:p-12 hard-shadow overflow-hidden"
          >
            {/* Ribbon badge */}
            <div className="absolute -right-14 top-7 rotate-45 bg-rust text-primary-foreground label-font text-[11px] tracking-[0.15em] px-16 py-1.5 hard-shadow-sm">
              FOUNDING PARTNER
            </div>
            <div className="flex items-center gap-3">
              <Award className="text-rust" size={26} />
              <span className="label-font text-xs text-rust tracking-[0.2em]">Limited Inaugural Opportunity</span>
            </div>
            <h2 className="mt-4 font-serif font-black text-3xl sm:text-4xl">Become a Founding Partner</h2>
            <p className="mt-5 max-w-3xl text-cream/85 text-base md:text-lg leading-relaxed">
              The inaugural sponsors of the Heroes & Legacy Golf Classic will forever be recognized as the companies that helped launch a tradition dedicated to honoring service and supporting first responders.
            </p>
            <p className="mt-4 max-w-3xl text-cream/70 leading-relaxed">
              As this event grows each year, your organization will remain part of its story and its legacy.
            </p>
            <div data-testid="founding-deadline" className="mt-6 inline-flex items-start gap-3 border border-rust/60 bg-rust/15 px-4 py-3 max-w-3xl">
              <Award size={18} className="text-rust shrink-0 mt-0.5" />
              <p className="text-sm text-cream leading-relaxed"><span className="font-semibold">Deadline: {SPONSOR_DEADLINES.foundingDate}.</span> {SPONSOR_DEADLINES.foundingText}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SPONSOR WALL */}
      <section className="bg-espresso text-cream overflow-hidden border-y border-cream/10">
        <style>{`
          .wwf-marquee { width: 100%; overflow: hidden; -webkit-mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent); mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent); }
          .wwf-track { display: flex; gap: 1.5rem; width: max-content; animation: wwf-scroll 38s linear infinite; }
          .wwf-marquee:hover .wwf-track { animation-play-state: paused; }
          @keyframes wwf-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
          .wwf-slot { width: 216px; height: 112px; flex: 0 0 auto; }
        `}</style>
        {partners.length > 0 ? (
          <>
            <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-2 text-center">
              <SectionLabel className="justify-center">Founding Partners</SectionLabel>
              <h2 className="mt-5 font-serif font-black text-3xl sm:text-4xl">The Companies Standing With Those Who Serve</h2>
              <p className="mt-4 text-cream/75 leading-relaxed">Permanently recognized as the partners who helped launch the Heroes & Legacy Golf Classic.</p>
            </div>
            <div className={partners.length > 4 ? "wwf-marquee py-10" : "py-10"} data-testid="founding-partners-wall">
              <div className={partners.length > 4 ? "wwf-track" : "flex flex-wrap justify-center gap-6 px-5"}>
                {(partners.length > 4 ? [...partners, ...partners] : partners).map((p, i) => {
                  const inner = (
                    <>
                      {p.logo_data ? (
                        <img src={p.logo_data} alt={p.name} className="max-h-14 max-w-[170px] object-contain" />
                      ) : (
                        <span className="font-serif font-bold text-lg text-cream text-center px-3 leading-tight">{p.name}</span>
                      )}
                      {p.tier_label && <span className="label-font text-[9px] text-rust tracking-[0.14em] mt-2">{p.tier_label}</span>}
                    </>
                  );
                  const cls = "wwf-slot border border-cream/20 bg-cream/[0.04] flex flex-col items-center justify-center p-4 transition-colors hover:border-rust/70";
                  return p.website ? (
                    <a key={`${p.id}-${i}`} href={p.website} target="_blank" rel="noopener noreferrer" data-testid={`partner-${p.id}`} className={cls}>{inner}</a>
                  ) : (
                    <div key={`${p.id}-${i}`} data-testid={`partner-${p.id}`} className={cls}>{inner}</div>
                  );
                })}
              </div>
            </div>
            <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 text-center">
              <a href="#sponsorship-tiers" data-testid="reserve-spot-btn" className="inline-flex items-center gap-2 label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-cream/20 transition-transform hover:-translate-y-1 hard-shadow">
                Join Them — Become a Founding Partner <ArrowRight size={16} />
              </a>
            </div>
          </>
        ) : (
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 md:py-20 text-center" data-testid="founding-partners-cta">
            <SectionLabel className="justify-center">Founding Partners</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-3xl sm:text-5xl leading-tight">Be the First Name on This Wall.</h2>
            <p className="mt-5 max-w-2xl mx-auto text-cream/75 text-base md:text-lg leading-relaxed">
              The inaugural partners of the Heroes & Legacy Golf Classic will be featured here — and in every program, for every year to come. Commit by {SPONSOR_DEADLINES.foundingDate} to claim permanent Founding Partner recognition.
            </p>
            <a href="#sponsorship-tiers" data-testid="reserve-spot-btn" className="mt-9 inline-flex items-center gap-2 label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-cream/20 transition-transform hover:-translate-y-1 hard-shadow">
              Become a Founding Partner <ArrowRight size={16} />
            </a>
          </div>
        )}
      </section>

      {/* TRANSITION TO TIERS */}
      <section className="paper-noise bg-background">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 md:pt-20 text-center relative z-10">
          <SectionLabel className="justify-center">Sponsorship Levels</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-3xl sm:text-4xl text-espresso leading-tight">
            Choose the Sponsorship Opportunity That Reflects Your Commitment
          </h2>
          <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed">
            Every sponsorship represents an investment in leadership, service, community, and the people who dedicate their lives to protecting others. Whether you become a Legacy Sponsor or support the Foundation at another level, your partnership helps build something that extends far beyond a single day of golf.
          </p>
          <a
            href="#sponsorship-tiers"
            data-testid="explore-sponsorships-btn"
            className="mt-9 inline-flex items-center gap-2 label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-espresso transition-transform hover:-translate-y-1 hard-shadow-sm"
          >
            Explore Sponsorship Opportunities <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
